import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG } from '../config/api.config';
import { environment } from '../../../environments/environment';

export interface EventImage {
  cuadrada: string | null;
  horizontal: string | null;
  banner: string | null;
}

export interface EventConfig {
  mostrar_aforo_disponible: boolean;
  permitir_venta: boolean;
  permitir_lista_espera: boolean;
}

export interface EventLocalizacion {
  lat: number;
  lng: number;
}

export interface EventCanal {
  canal: string;
  publicado: boolean;
  disponible: boolean;
}

export interface Event {
  eventoid: string;
  organizadorid: string;
  nombre: string;
  url_slug: string;
  url_completa: string;
  lugar: string;
  categoria: string;
  categoriaeventoid?: string;
  fecha_hora: string;
  timezone: string;
  tipoevento?: string; // 'default' | 'tren'
  imagenes: EventImage;
  descripcion: string;
  contenido_html?: string;
  localizacion?: EventLocalizacion;
  cargo_servicio: number;
  estado: string;
  visibilidad: string;
  destacado: boolean;
  configuracion: EventConfig;
  tiempo_expiracion_reserva: number;
  max_tickets_por_compra?: number;
  permitir_validacion_ticket: boolean;
  canales: EventCanal[];
  permitir_factura_electronica: boolean;
  tipo_facturacion: string;
  ticket_facturadorid: string;
  cargo_servicio_facturadorid: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EventsResponse {
  success: boolean;
  data: Event[];
}

export interface EventDetailResponse {
  success: boolean;
  data: Event;
}

export interface SubeventoImagen {
  cuadrada: string | null;
  horizontal: string | null;
  banner: string | null;
  actividad: string | null;
}

export interface SubeventoCanal {
  canal: string;
  disponible: boolean;
}

export interface Subevento {
  subeventoid: string;
  eventoid: string;
  nombre: string;
  slug: string;
  descripcion: string;
  contenido_html: string;
  imagenes: SubeventoImagen;
  canales: SubeventoCanal[];
  estado: string;
  createdAt: string;
  updatedAt: string;
}

export interface SubeventosResponse {
  success: boolean;
  data: Subevento[];
}

export interface SubeventoResponse {
  success: boolean;
  data: Subevento;
}

export interface FechasResponse {
  success: boolean;
  data: {
    fechas: string[];
    timezone: string;
  };
}

export interface TipoTicketFuncion {
  eventofuncionid: string;
  eventoid: string;
  subeventoid: string;
  tipoticketid: string;
  fecha: string;
  hora: string;
  fecha_funcion: string;
  fecha_funcion_maxima_disponible_web: string;
  fecha_funcion_maxima_disponible_boxoffice: string;
}

export interface TipoTicketConFuncion {
  tipoticketid: string;
  eventoid: string;
  nombre_localidad: string;
  aforo_maximo: number;
  aforo_disponible: number;
  estado: string;
  eventoubicacionid: string;
  seatmapid: string | null;
  precios: any[];
  tickets: any[];
  tickets_reservados: number;
  funcion: TipoTicketFuncion;
  subevento: { subeventoid: string; nombre: string; slug: string };
  ubicacion: { eventoubicacionid: string; nombre_ubicacion: string; descripcion: string } | null;
}

export interface TipoTicketsByFechaResponse {
  success: boolean;
  message: string;
  data: TipoTicketConFuncion[];
}

@Injectable({
  providedIn: 'root',
})
export class EventsService {
  constructor(private http: HttpClient) {}

  private isWebActivePrice(precio: any): boolean {
    if (!precio || (precio.canal || '').toLowerCase() !== 'web') {
      return false;
    }

    const estadoActivo = (precio.estado || '').toLowerCase() === 'activo';
    const publicado = precio.publicado === true;
    const disponible = precio.disponible === true;

    return estadoActivo && publicado && disponible;
  }

  private normalizeCategories(categories?: string[]): string[] {
    if (!Array.isArray(categories)) return [];
    return categories
      .map((category) => (category || '').trim().toLowerCase())
      .filter(Boolean);
  }

  private getEventCategoryId(event?: Event): string {
    return (event?.categoriaeventoid || event?.categoria || '').trim().toLowerCase();
  }

  private isEventAllowedByCategory(event?: Event): boolean {
    if (!event) return false;

    const include = this.normalizeCategories(environment.category_include);
    const exclude = this.normalizeCategories(environment.category_exclude);
    const eventCategory = this.getEventCategoryId(event);

    if (include.length > 0 && !include.includes(eventCategory)) {
      return false;
    }

    if (exclude.includes(eventCategory)) {
      return false;
    }

    return true;
  }

  private isEventEnabledForWeb(event?: Event): boolean {
    if (!event) {
      return false;
    }

    const canalWeb = (event.canales || []).find((canal) => (canal?.canal || '').toLowerCase() === 'web');
    if (!canalWeb) {
      return false;
    }

    // Backward compatibility with legacy payloads that used estado/disponibilidad strings.
    const canalWebLegacy = canalWeb as any;
    const isPublicado = canalWeb.publicado === true || canalWebLegacy?.estado === 'publicado';
    const isDisponible = canalWeb.disponible === true || canalWebLegacy?.disponibilidad === 'disponible';

    return isPublicado && isDisponible;
  }

  private isEventAllowedForWeb(event?: Event): boolean {
    return this.isEventAllowedByCategory(event) && this.isEventEnabledForWeb(event);
  }

  /**
   * Obtiene todos los eventos públicos
   */
  getPublicEvents(): Observable<EventsResponse> {
    console.log(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.eventos.listPublic}`);
    return this.http.get<EventsResponse>(
      `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.eventos.listPublic}`,
      {
        headers: {
          'x-api-key': API_CONFIG.api_key
        }
      }
    ).pipe(
      map((response) => {
        if (!response?.success || !Array.isArray(response.data)) {
          return response;
        }

        return {
          ...response,
          data: response.data.filter((event) => this.isEventAllowedForWeb(event)),
        };
      })
    );
  }

  /**
   * Obtiene un evento por su url_slug
   */
  getEventBySlug(slug: string): Observable<EventDetailResponse> {
    const url = API_CONFIG.endpoints.eventos.getBySlug.replace('{url_slug}', slug);
    return this.http.get<EventDetailResponse>(`${API_CONFIG.baseUrl}${url}`, {
      headers: {
        'x-api-key': API_CONFIG.api_key
      }
    }).pipe(
      map((response) => {
        if (!response?.success || !response.data) {
          return response;
        }

        if (this.isEventAllowedForWeb(response.data)) {
          return response;
        }

        throw new HttpErrorResponse({
          status: 404,
          statusText: 'Not Found',
          url: `${API_CONFIG.baseUrl}${url}`,
          error: { message: 'Event not found' },
        });
      })
    );
  }

  /**
   * Obtiene la imagen por defecto según el tipo
   */
  getDefaultImage(type: 'cuadrada' | 'horizontal' | 'banner'): string {
    const defaults = {
      cuadrada: 'https://dummyimage.com/1080x1080/cccccc/666666?text=Evento',
      horizontal: 'https://dummyimage.com/1200x630/cccccc/666666?text=Evento',
      banner: 'https://dummyimage.com/2000x576/cccccc/666666?text=Evento'
    };
    return defaults[type];
  }

  /**
   * Obtiene la imagen del evento o una por defecto
   */
  getEventImage(event: Event, type: 'cuadrada' | 'horizontal' | 'banner'): string {
    return event.imagenes[type] || this.getDefaultImage(type);
  }

  /**
   * Obtiene los subeventos (actividades) activos de un evento
   */
  getSubeventos(eventoid: string): Observable<SubeventosResponse> {
    const url = API_CONFIG.endpoints.eventos.getSubeventos.replace('{eventoid}', eventoid);
    console.log('Fetching subeventos with URL:', `${API_CONFIG.baseUrl}${url}`);
    return this.http.get<SubeventosResponse>(`${API_CONFIG.baseUrl}${url}`, {
      headers: { 'x-api-key': API_CONFIG.api_key }
    });
  }

  /**
   * Obtiene un subevento por su slug dentro de un evento
   */
  getSubeventoBySlug(eventoid: string, slug: string): Observable<SubeventoResponse> {
    const url = API_CONFIG.endpoints.eventos.getSubeventoBySlug
      .replace('{eventoid}', eventoid)
      .replace('{slug}', slug);
    return this.http.get<SubeventoResponse>(`${API_CONFIG.baseUrl}${url}`, {
      headers: { 'x-api-key': API_CONFIG.api_key }
    });
  }

  /**
   * Obtiene las fechas disponibles para un subevento (desde hoy)
   */
  getSubeventoFechas(eventoid: string, subeventoid: string): Observable<FechasResponse> {
    const url = API_CONFIG.endpoints.eventos.getSubeventoFechas
      .replace('{eventoid}', eventoid)
      .replace('{subeventoid}', subeventoid);
    return this.http.get<FechasResponse>(`${API_CONFIG.baseUrl}${url}`, {
      headers: { 'x-api-key': API_CONFIG.api_key }
    });
  }

  /**
   * Obtiene los tipos de ticket disponibles para una fecha y subevento específicos
   */
  getTipoTicketsByFecha(eventoid: string, subeventoid: string, fecha: string): Observable<TipoTicketsByFechaResponse> {
    const url = API_CONFIG.endpoints.eventos.getSubeventoTipoTicketsByFecha
      .replace('{eventoid}', eventoid)
      .replace('{subeventoid}', subeventoid)
      .replace('{fecha}', fecha);
    return this.http.get<TipoTicketsByFechaResponse>(`${API_CONFIG.baseUrl}${url}`, {
      headers: { 'x-api-key': API_CONFIG.api_key }
    }).pipe(
      map((response) => {
        if (!response?.success || !Array.isArray(response.data)) {
          return response;
        }

        return {
          ...response,
          data: response.data,
        };
      })
    );
  }
}
